import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

/* ─── Types ──────────────────────────────────────────────────────────────────── */

interface WbButton { title: string; payload?: string; url?: string; type?: string; }
interface WbElement { title: string; subtitle?: string; image_url?: string; buttons?: WbButton[]; }
interface WbMessage { type: 'text' | 'buttons' | 'carousel'; text?: string; buttons?: WbButton[]; elements?: WbElement[]; }
interface ChatEntry { role: 'user' | 'bot'; text?: string; buttonLabel?: string; messages?: WbMessage[]; timestamp: Date; }
interface Props { visible: boolean; onClose: () => void; }

// ─── SVG helpers ──────────────────────────────────────────────────────────────

const WaIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{width: 20, height: 20}}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787" />
  </svg>
);

const DoubleTickIcon = () => (
  <svg viewBox="0 0 16 11" fill="currentColor" style={{width: 14, height: 10, color: '#53BDEB', display: 'inline-block', marginLeft: 4}}>
    <path d="M11.071.653a.375.375 0 0 0-.531.02L5.85 5.765 3.84 3.493a.375.375 0 1 0-.567.492l2.302 2.605a.375.375 0 0 0 .549.014L11.09 1.18a.375.375 0 0 0-.019-.528zM15.071.653a.375.375 0 0 0-.531.02L9.85 5.765l-.47-.531a.375.375 0 1 0-.566.492l.75.849a.375.375 0 0 0 .548.014L15.09 1.18a.375.375 0 0 0-.019-.528zM.379 6.1a.375.375 0 0 0 0 .75h1.5a.375.375 0 0 0 0-.75H.379z" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{width: 20, height: 20}}>
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const AttachmentIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M18.9 12l-6.7 6.6c-1.6 1.6-4.3 1.6-5.8 0-1.6-1.6-1.6-4.2 0-5.8l7-7c.9-.9 2.4-.9 3.3 0 .9.9.9 2.4 0 3.3l-6.5 6.6c-.4.4-1.1.4-1.5 0-.4-.4-.4-1.1 0-1.5l5.3-5.3-1.1-1-5.3 5.3c-1 1-1 2.7 0 3.7 1 1 2.7 1 3.7 0l6.5-6.6c1.5-1.5 1.5-3.9 0-5.4-1.5-1.5-3.9-1.5-5.4 0l-7 7c-2.2 2.2-2.2 5.7 0 7.9 2.2 2.2 5.7 2.2 7.9 0l6.7-6.6-1.1-1.2z"/>
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const renderWA = (text: string) => {
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('*') && part.endsWith('*')) {
      return <strong key={i}>{part.slice(1, -1)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
};

const fmt = (d: Date) =>
  d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });

const WA_BG = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8b8a0' fill-opacity='0.18'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

/* ── Product card (vertical) ────────────────────────────────── */

const ProductCard: React.FC<{el: WbElement; onBtn:(p:string,t:string)=>void; disabled:boolean}> = ({el, onBtn, disabled}) => {
  const isLink = !el.image_url && el.buttons?.[0]?.type === 'web_url';
  return (
    <div style={{background:'#fff', borderTop: '1px solid #f0f0f0', overflow:'hidden'}}>
      {el.image_url && (
        <div style={{padding: '0 10px 10px'}}>
           <img src={el.image_url} alt={el.title}
            style={{width:'100%', height:180, objectFit:'cover', display:'block', borderRadius: 8, marginTop: 10}}
            onError={e=>{(e.target as HTMLImageElement).style.display='none'}}/>
        </div>
      )}
      <div style={{padding:'0 12px 10px'}}>
        <div style={{fontWeight:600, fontSize:14, color:'#111', lineHeight:1.4, marginBottom:4}}>{el.title}</div>
        {el.subtitle && el.subtitle.split('\n').map((line,i)=>(
          <div key={i} style={{fontSize:13, color: i===0 ? '#1a7f37' : '#666', lineHeight:1.5, fontWeight: i===0 ? 600 : 400}}>{line}</div>
        ))}
      </div>
      {el.buttons && el.buttons.length > 0 && (
        <div style={{borderTop:'1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 6, padding: '10px'}}>
          {el.buttons.map((btn,i) => isLink
            ? <a key={i} href={btn.url} target="_blank" rel="noopener noreferrer"
                style={{display:'flex', alignItems:'center', justifyContent:'center', padding:'10px', color:'#128C7E', fontWeight:600, fontSize:13, textDecoration:'none', gap:8, border: '1px solid rgba(18,140,126,0.3)', borderRadius: 8, background: 'rgba(18,140,126,0.05)'}}>
                <svg viewBox="0 0 24 24" fill="currentColor" style={{width:16,height:16}}><path d="M19 19H5V5h7V3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/></svg>
                {btn.title}
              </a>
            : <button key={i} disabled={disabled}
                onClick={()=>btn.payload && onBtn(btn.payload, btn.title)}
                style={{display:'flex',alignItems:'center',justifyContent:'center',width:'100%',padding:'10px',background:'rgba(18,140,126,0.05)',border:'1px solid rgba(18,140,126,0.3)',borderRadius: 8,cursor:disabled?'not-allowed':'pointer',color:'#128C7E',fontWeight:600,fontSize:13,gap:8}}>
                <svg viewBox="0 0 20 20" fill="currentColor" style={{width: 14, height: 14, opacity: 0.7}}>
                    <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
                </svg>
                {btn.title}
              </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Bot bubble content ────────────────────────────────────── */

const BotContent: React.FC<{msg:WbMessage; onBtn:(p:string,t:string)=>void; disabled:boolean}> = ({msg, onBtn, disabled}) => {
  if (msg.type === 'text') {
    return <p style={{margin:0, fontSize:13.5, color:'#111', whiteSpace:'pre-wrap', wordBreak:'break-word', lineHeight:1.5}}>{renderWA(msg.text||'')}</p>;
  }
  if (msg.type === 'buttons') {
    return (
      <div style={{padding: '2px 0'}}>
        {msg.text && <p style={{margin:'0 0 10px', fontSize:13.5, color:'#111', lineHeight:1.5, whiteSpace:'pre-wrap'}}>{renderWA(msg.text)}</p>}
        <div style={{display:'flex', flexDirection:'column', gap: 6}}>
          {msg.buttons?.map((btn,i)=>(
            <button key={i} disabled={disabled} onClick={()=>onBtn(btn.payload||btn.title, btn.title)}
              style={{
                background:'rgba(18,140,126,0.05)', 
                border: '1px solid rgba(18,140,126,0.3)',
                borderRadius: 8,
                padding:'8px 12px', 
                color:'#128C7E', 
                fontWeight:600, 
                fontSize:13, 
                cursor:disabled?'not-allowed':'pointer', 
                textAlign:'center',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                transition: 'all 0.2s'
              }}
            >
              <svg viewBox="0 0 20 20" fill="currentColor" style={{width: 14, height: 14, opacity: 0.7}}>
                <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
              </svg>
              {btn.title}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (msg.type === 'carousel' && msg.elements) {
    return (
      <div style={{margin: '-7px -10px -5px'}}>
        {msg.text && <p style={{margin:'0', padding: '7px 10px 5px', fontSize:13.5, color:'#111', lineHeight:1.5}}>{renderWA(msg.text)}</p>}
        {msg.elements.map((el,i)=><ProductCard key={i} el={el} onBtn={onBtn} disabled={disabled}/>)}
      </div>
    );
  }
  return null;
};

/* ── Main ──────────────────────────────────────────────────── */

const WhatsAppBotSimulator: React.FC<Props> = ({visible, onClose}) => {
  const [phone, setPhone] = useState('');
  const [draftPhone, setDraftPhone] = useState('');
  const [started, setStarted] = useState(false);
  const [chat, setChat] = useState<ChatEntry[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string|null>(null);
  const [webhookUrl] = useState('http://localhost:5005/webhook');
  const [showAttach, setShowAttach] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(()=>{
    if(!visible){setPhone('');setDraftPhone('');setStarted(false);setChat([]);setInput('');setError(null);setShowAttach(false);}
  },[visible]);

  useEffect(()=>{
    setTimeout(()=>bottomRef.current?.scrollIntoView({behavior:'smooth'}),100);
  },[chat,sending]);

  useEffect(()=>{
    if(started) setTimeout(()=>inputRef.current?.focus(), 150);
  },[started]);

  if(!visible) return null;

  const sendMsg = async (text: string, label?: string) => {
    if(!text.trim()||sending) return;
    setError(null);
    setSending(true);
    setShowAttach(false);
    setChat(p=>[...p,{role:'user',text,buttonLabel:label,timestamp:new Date()}]);
    setInput('');
    try {
      const res = await axios.post(webhookUrl, {sender_id:phone, message:text, metadata:{phone}});
      const msgs: WbMessage[] = res.data?.messages || [];
      setChat(p=>[...p,{role:'bot',messages:msgs,timestamp:new Date()}]);
    } catch(e:any) {
      const m = e?.response?.data?.detail || e?.message || 'Request failed';
      setError(m);
      setChat(p=>[...p,{role:'bot',messages:[{type:'text',text:`⚠️ ${m}`}],timestamp:new Date()}]);
    } finally { setSending(false); }
  };

  const start = () => {
    const t = draftPhone.trim();
    if(!t) return;
    setPhone(t); setStarted(true); setChat([]);
  };

  return (
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}}
      style={{position:'fixed',inset:0,zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.75)',padding:16}}>
      <div onClick={e=>e.stopPropagation()}
        style={{display:'flex',flexDirection:'column',width:440,maxHeight:'92vh',minHeight:560,borderRadius:16,overflow:'hidden',boxShadow:'0 24px 80px rgba(0,0,0,0.55)',fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif"}}>

        {/* WhatsApp Header */}
        <div style={{background:'#075E54',display:'flex',alignItems:'center',gap:12,padding:'12px 16px',flexShrink:0,userSelect:'none'}}>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#fff',opacity:0.7,cursor:'pointer',padding:0,display:'flex',alignItems:'center'}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{width: 20, height: 20}}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div style={{width:36,height:36,borderRadius:'50%',background:'#128C7E',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0, color: '#fff'}}>
            <svg viewBox="0 0 24 24" fill="currentColor" style={{width:20,height:20}}>
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
            </svg>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{color:'#fff',fontWeight:600,fontSize:14,lineHeight:1.1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
              {started ? `+91 ${phone}` : 'Moglix Bot'}
            </div>
            <div style={{color:'#9fdfcd',fontSize:11,marginTop:2}}>
              {started ? (sending ? 'typing…' : 'online') : 'Customer Simulator'}
            </div>
          </div>
          <span style={{color: '#9fdfcd', opacity: 0.6}}><WaIcon /></span>
        </div>



        {/* Chat area */}
        <div style={{flex:1,overflowY:'auto',background:'#ECE5DD',backgroundImage:WA_BG,padding:'12px'}}>
          {!started ? (
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',gap:16,padding:'24px 0'}}>
              <div style={{width:64,height:64,borderRadius:'50%',background:'#25D366',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff'}}>
                <WaIcon />
              </div>
              <div style={{textAlign:'center'}}>
                <div style={{fontWeight:700,color:'#444',fontSize:16}}>WhatsApp Bot Simulator</div>
                <div style={{color:'#888',fontSize:12,marginTop:4,maxWidth:260}}>Enter the customer phone number to start simulating</div>
              </div>
              <div style={{background:'#fff',borderRadius:12,padding:16,width:'100%',maxWidth:300,boxShadow:'0 2px 12px rgba(0,0,0,0.08)',display:'flex',flexDirection:'column',gap:12}}>
                <label style={{fontSize:11,fontWeight:600,color:'#666',textTransform:'uppercase'}}>Customer Phone</label>
                <input type="tel" placeholder="e.g. 9415332242" value={draftPhone}
                  onChange={e=>setDraftPhone(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter')start();}}
                  autoFocus
                  style={{border:'1px solid #ddd',borderRadius:8,padding:'10px',fontSize:14,outline:'none'}}/>
                <button onClick={start} disabled={!draftPhone.trim()}
                  style={{background:draftPhone.trim()?'#25D366':'#a8d5b5',color:'#fff',border:'none',borderRadius:8,padding:'10px',fontWeight:700,fontSize:14,cursor:'pointer'}}>
                  Start Conversation
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{display:'flex',justifyContent:'center',marginBottom:10}}>
                <span style={{background:'rgba(255,255,255,0.7)',color:'#555',fontSize:10,padding:'2px 10px',borderRadius:12,boxShadow:'0 1px 1px rgba(0,0,0,0.05)'}}>Today</span>
              </div>
              <div style={{display:'flex',justifyContent:'center',marginBottom:15}}>
                <span style={{background:'#FFF3CD',color:'#856404',fontSize:11,padding:'5px 12px',borderRadius:8,textAlign:'center',maxWidth:280,boxShadow:'0 1px 1px rgba(0,0,0,0.05)'}}>
                  🤖 Simulating as <strong>{phone}</strong> — type a message or tap a button reply
                </span>
              </div>

              {chat.map((entry,idx)=>{
                const isUser = entry.role==='user';
                return (
                  <div key={idx} style={{display:'flex',justifyContent:isUser?'flex-end':'flex-start',marginBottom:2, position: 'relative'}}>
                    <div style={{
                      maxWidth:'82%',
                      background:isUser?'#DCF8C6':'#fff',
                      borderRadius:isUser?'12px 2px 12px 12px':'2px 12px 12px 12px',
                      padding:'6px 10px 4px',
                      boxShadow:'0 1px 1px rgba(0,0,0,0.12)',
                      position:'relative',
                      fontSize: '13.5px'
                    }}>
                      {/* Tail */}
                      <span style={{
                          position: 'absolute',
                          top: 0,
                          [isUser ? 'right' : 'left']: -6,
                          width: 0,
                          height: 0,
                          borderStyle: 'solid',
                          borderWidth: isUser ? '0 0 8px 8px' : '0 8px 8px 0',
                          borderColor: isUser ? 'transparent transparent transparent #DCF8C6' : 'transparent #FFFFFF transparent transparent',
                      }} />

                      {isUser && entry.buttonLabel && (
                        <div style={{fontSize:10.5,color:'#128C7E',fontWeight:600,marginBottom:4,background:'rgba(0,0,0,0.03)',borderLeft:'3px solid #128C7E',padding:'3px 8px',borderRadius:'4px',textTransform:'uppercase'}}>
                          Button: {entry.buttonLabel}
                        </div>
                      )}
                      
                      {isUser ? (
                         <p style={{margin:0,color:'#111',wordBreak:'break-word',lineHeight:1.5}}>{entry.text}</p>
                      ) : (
                        entry.messages?.map((msg,mi)=>(
                          <BotContent key={mi} msg={msg} onBtn={(p,t)=>sendMsg(p,t)} disabled={sending}/>
                        ))
                      )}

                      <div style={{display:'flex',justifyContent:'flex-end',alignItems:'center',marginTop:2,gap:2}}>
                        <span style={{fontSize:10,color:'#888'}}>{fmt(entry.timestamp)}</span>
                        {isUser && <DoubleTickIcon/>}
                      </div>
                    </div>
                  </div>
                );
              })}

              {sending && (
                <div style={{display:'flex',justifyContent:'flex-start',marginBottom:2}}>
                  <div style={{background:'#fff',borderRadius:'2px 12px 12px 12px',padding:'10px 16px',boxShadow:'0 1px 1px rgba(0,0,0,0.12)'}}>
                    <div style={{display:'flex',gap:3}}>
                      {[0,150,300].map(d=><span key={d} style={{width:6,height:6,borderRadius:'50%',background:'#ccc',animation:`waBounce 0.8s ${d}ms infinite`}}/>)}
                    </div>
                  </div>
                </div>
              )}
              <div ref={bottomRef}/>
            </>
          )}
        </div>

        {/* Input bar */}
        <div style={{background:'#F0F0F0', borderTop:'1px solid #ddd', padding:'8px 12px', display:'flex', alignItems:'center', gap: 10, flexShrink:0}}>
          {!started ? (
            <div style={{flex:1,background:'rgba(255,255,255,0.6)',borderRadius:24,padding:'8px 16px',fontSize:12,color:'#999'}}>
              Start a session to chat…
            </div>
          ) : (
            <>
              <div style={{flex: 1, background: '#fff', borderRadius: 24, display: 'flex', alignItems: 'center', padding: '2px 10px', border: '1px solid #e0e0e0'}}>
                <input ref={inputRef} type="text" placeholder="Type a message…" value={input}
                  onChange={e=>setInput(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey&&input.trim()){e.preventDefault();sendMsg(input.trim());}}}
                  disabled={sending}
                  style={{flex:1, border:'none', padding:'8px 5px', fontSize:13.5, color:'#111', outline:'none', background: 'transparent'}}/>
                <button onClick={()=>setShowAttach(!showAttach)} style={{background:'none', border:'none', color:'#8696a0', cursor:'pointer', display: 'flex'}}><AttachmentIcon/></button>
              </div>
              <button onClick={()=>input.trim() ? sendMsg(input.trim()) : null} 
                disabled={!input.trim() || sending}
                style={{width:42, height:42, borderRadius:'50%', background:input.trim()?'#25D366':'#a8d5b5', border:'none', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, boxShadow:'0 1px 2px rgba(0,0,0,0.1)'}}>
                <SendIcon/>
              </button>
            </>
          )}
        </div>

        {error && (
          <div style={{background:'#fef2f2',borderTop:'1px solid #fecaca',padding:'6px 12px',fontSize:11,color:'#dc2626',flexShrink:0}}>⚠️ {error}</div>
        )}
      </div>

      {/* Attachment Menu Popup */}
      {showAttach && (
        <div onClick={()=>setShowAttach(false)} style={{position:'fixed', inset:0, zIndex:10000}}>
          <div onClick={e=>e.stopPropagation()} style={{position: 'absolute', bottom: '85px', left: '50%', transform: 'translateX(-50%)', background: '#fff', borderRadius: 16, padding: '20px', boxShadow: '0 -5px 30px rgba(0,0,0,0.2)', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', width: '280px'}}>
            {[
              {icon: <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>, label: 'Document', color: '#7f66ff'},
              {icon: <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>, label: 'Camera', color: '#ff2e74'},
              {icon: <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>, label: 'Gallery', color: '#c07ed9'},
              {icon: <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>, label: 'Audio', color: '#ff8a00'},
              {icon: <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>, label: 'Location', color: '#069002'},
              {icon: <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>, label: 'Contact', color: '#02a5e4'}
            ].map(item => (
              <div key={item.label} style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8}}>
                <div style={{width: 54, height: 54, borderRadius: '50%', background: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer', boxShadow: '0 3px 6px rgba(0,0,0,0.1)'}}>
                  <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">{item.icon}</svg>
                </div>
                <span style={{fontSize: 12, color: '#666', fontWeight: 500}}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes waBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
      `}</style>
    </div>
  );
};

export default WhatsAppBotSimulator;
